import React from "react";

export type StatTone = "green" | "orange" | "purple" | "red";

const TONES: Record<StatTone, { border: string; glow: string; ring: string; icon: string }> = {
  green: {
    border: "rgba(45,204,137,0.24)",
    glow: "rgba(45,204,137,0.45)",
    ring: "#07a66c",
    icon: "text-[#00c27e]",
  },
  orange: {
    border: "rgba(249,115,22,0.24)",
    glow: "rgba(249,115,22,0.45)",
    ring: "#f97316",
    icon: "text-[#fb923c]",
  },
  purple: {
    border: "rgba(168,85,247,0.24)",
    glow: "rgba(168,85,247,0.45)",
    ring: "#a855f7",
    icon: "text-[#c084fc]",
  },
  red: {
    border: "rgba(239,68,68,0.24)",
    glow: "rgba(239,68,68,0.45)",
    ring: "#ef4444",
    icon: "text-[#f87171]",
  },
};

interface StatCardProps {
  tone: StatTone;
  /** A lucide icon element or an image path from /public. */
  icon: React.ReactNode | string;
  value: React.ReactNode;
  label: string;
  /** Shown to the right of the value, e.g. a weekly change. */
  extra?: React.ReactNode;
  /** Right-hand slot (badge and mini chart in the redesign). */
  aside?: React.ReactNode;
  /** md: mobile tile (16 px icon); lg: desktop tile (32 px icon, 18 px value). */
  size?: "md" | "lg";
  title?: string;
  className?: string;
}

/** Stat tile with the redesign's coloured edge glow and ringed icon. */
export const StatCard: React.FC<StatCardProps> = ({
  tone,
  icon,
  value,
  label,
  extra,
  aside,
  size = "md",
  title,
  className = "",
}) => {
  const t = TONES[tone];
  const lg = size === "lg";
  return (
    <div
      title={title}
      className={`edge-glow relative flex shrink-0 items-center overflow-hidden rounded-xl border bg-primary ${
        lg ? "gap-5 py-3 pl-6 pr-3" : "gap-5 py-2 pl-4 pr-3"
      } ${className}`}
      style={{ borderColor: t.border, ["--glow" as string]: t.glow }}
    >
      <span
        className={`relative flex shrink-0 items-center justify-center ${lg ? "size-8" : "size-4"} ${t.icon}`}
      >
        <span
          className={`absolute rounded-full border-2 opacity-30 ${lg ? "-inset-[5px]" : "-inset-1"}`}
          style={{ borderColor: t.ring }}
        />
        <span
          className={`absolute rounded-full border-2 opacity-10 ${lg ? "-inset-[10px]" : "-inset-[9px]"}`}
          style={{ borderColor: t.ring }}
        />
        {typeof icon === "string" ? (
          <img src={icon} alt="" className="size-full" />
        ) : (
          icon
        )}
      </span>
      <div className="flex min-w-0 flex-1 flex-col whitespace-nowrap">
        <div className="flex items-baseline gap-1.5">
          <span
            className={`font-semibold text-white ${lg ? "text-lg leading-7" : "text-base leading-6"}`}
          >
            {value}
          </span>
          {extra}
        </div>
        <span
          className={`truncate text-[#d4d4d4] ${lg ? "text-sm leading-5" : "text-xs leading-[18px]"}`}
        >
          {label}
        </span>
      </div>
      {aside}
    </div>
  );
};
